import React, { useEffect, useRef, useState } from 'react'
import Icon, { IconName, IconSize, IconColor } from 'particles/Icon'
import Text, { TextColor, TextScale } from 'particles/Text'
import { menuItemClasses } from '@mui/material/MenuItem'
import styled from 'particles/themes/styled'
import { BaseMenuItemProps, MenuItemSize } from '.'
import { StyledBaseMenuItem } from './style'
import IconButton from 'particles/Button/IconButton'
import Tooltip from 'particles/Tooltip'

const sizeMap = (selected: boolean): Record<MenuItemSize, { iconSize: IconSize; textScale: TextScale }> => ({
    m: {
        iconSize: '20px',
        textScale: selected ? 'bodyM700' : 'bodyM400',
    },
    s: {
        iconSize: '16px',
        textScale: selected ? 'bodyS700' : 'bodyS400',
    },
})

export interface MenuItemProps extends BaseMenuItemProps {
    icon?: IconName
    iconColor?: IconColor
    text: string
    textColor?: TextColor
    description?: string
    isRichContentItem?: boolean
    dataTestId?: string
}

const StyledMenuItem = styled(StyledBaseMenuItem)`
    &.${menuItemClasses.root} {
        padding: 6px 4px;
        align-self: stretch;
        &.${menuItemClasses.focusVisible} {
            border: 3px solid ${({ theme }) => theme.colors.border.actionFocus};
            padding: 3px 1px;
            background-color: transparent;
        }
        .option {
            flex: 1 0 0;
            white-space: pre-wrap;
        }
        .icon {
            padding: 2px 0px;
            flex-shrink: 0;
        }
    }
    .menu-item-area {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
    }
    .menu-item-title-area {
        display: flex;
        gap: ${({ theme }) => theme.spacing[100]};
        align-items: flex-start;
    }
    .description {
        white-space: pre-wrap;
        word-break: break-word;
    }
    .menu-item-area,
    .option,
    .description {
        cursor: pointer;
    }
    .disabled {
        cursor: not-allowed;
    }
`

const MenuItemText: React.FC<MenuItemProps> = ({
    text,
    description,
    icon,
    iconColor,
    isDisabled,
    size = 's',
    selected = false,
    tooltipContext,
    textColor,
    isRichContentItem = false,
    dataTestId,
    ...props
}) => {
    const { iconSize, textScale } = sizeMap(selected)[size]
    const labelRef = useRef<HTMLInputElement>(null)
    // This will be use to calculate alignItems
    const [offsetHeight, setOffsetHeight] = useState(labelRef.current?.offsetHeight)

    useEffect(() => {
        setOffsetHeight(labelRef.current?.offsetHeight)
    }, [labelRef.current?.offsetHeight])

    return (
        <StyledMenuItem
            {...props}
            disabled={isDisabled}
            disableRipple
            containerHeight={offsetHeight}
            textScale={textScale}
            data-testid={dataTestId}
        >
            <div className="menu-item-area">
                <div className="menu-item-title-area">
                    {icon && (
                        <Icon
                            name={icon}
                            size={iconSize}
                            color={isDisabled ? 'disable' : iconColor}
                            className={`icon ${isDisabled ? 'disabled' : ''}`}
                        />
                    )}
                    <Text
                        ref={labelRef}
                        scale={isRichContentItem ? 'bodyS700' : textScale}
                        className={`option ${isDisabled ? 'disabled' : ''}`}
                        color={textColor}
                        {...(isDisabled && { color: 'disable' })}
                    >
                        {text}
                    </Text>
                </div>
                {description && (
                    <Text
                        scale="bodyS400"
                        color={isDisabled ? 'disable' : 'secondary'}
                        className={`description ${isDisabled ? 'disabled' : ''}`}
                    >
                        {description}
                    </Text>
                )}
            </div>
            {isDisabled && (tooltipContext?.label || tooltipContext?.title) && (
                <Tooltip {...tooltipContext} onClick={e => e.stopPropagation()}>
                    <IconButton size="xs" color="neutral" form="icon" icon="question" className="tooltip" />
                </Tooltip>
            )}
        </StyledMenuItem>
    )
}

export default MenuItemText
